import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import UserGroupService from "../../services/api/userGroupService";
import { setSessionExpired, setTotalPage } from "./globalSlice";
import { setIsActivatePopup, setIsEditpopup } from "./popUpSlice";
import { unauthorizedError } from "../../utils/helpers";

// Create an instance of the service
const userGroupService = new UserGroupService();

// ------------------------------------
// Thunks
// ------------------------------------

// 1. Fetch all groups (with pagination, search, etc.)
export const getAllGroupsData = createAsyncThunk(
  "/broadcast/groups", // Thunk type name (unique string)
  async (
    { page, limit, searchQuery, sortKey, sortDirection, navigate },
    { dispatch }
  ) => {
    try {
      // call the service
      const response = await userGroupService.getAllGroups({
        page,
        limit,
        searchQuery,
        sortKey,
        sortDirection,
        navigate,
      });

      // If the backend response includes total pages, set it
      // Otherwise, update this as needed
      // e.g., if the backend returns response.total, we do:
      // dispatch(setTotalPage(response?.total));
      dispatch(setTotalPage(response?.total || 1));

      return response;
    } catch (error) {
      toast.error(error?.data?.error);
      // Handle 401 or other errors
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error("Something went wrong while fetching user groups.");
      }
      throw error;
    }
  }
);

// 2. Create a new group
export const createGroupData = createAsyncThunk(
  "/broadcast/group/create",
  async (
    { body, page, limit, searchQuery, sortKey, sortDirection, navigate },
    { dispatch }
  ) => {
    try {
      const response = await userGroupService.createGroup(body, navigate);

      // refresh the list after creation
      dispatch(
        getAllGroupsData({
          page,
          limit,
          searchQuery: "",
          sortKey,
          sortDirection,
          navigate,
        })
      );

      toast.success("Group created successfully!");
      // If you want to close any popups:
      dispatch(setIsActivatePopup(false));
      dispatch(setIsEditpopup(false));

      return response;
    } catch (error) {
      toast.error(error?.data?.error);
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message
        );
      }
      throw error?.data?.message;
    }
  }
);

// 3. Update an existing group
export const updateGroupData = createAsyncThunk(
  "/broadcast/group/update",
  async (
    { id, body, page, limit, searchQuery, sortKey, sortDirection, navigate },
    { dispatch }
  ) => {
    try {
      const response = await userGroupService.updateGroup({
        id,
        body,
        navigate,
      });

      // refresh the list after update
      dispatch(
        getAllGroupsData({
          page,
          limit,
          searchQuery: "",
          sortKey,
          sortDirection,
          navigate,
        })
      );

      toast.success("Group updated successfully!");
      // If you want to close any popups:
      dispatch(setIsActivatePopup(false));
      dispatch(setIsEditpopup(false));

      return response;
    } catch (error) {
      toast.error(error?.data?.error);
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message
        );
      }
      throw error?.data?.message;
    }
  }
);

// 4. Delete group(s)
export const deleteGroupData = createAsyncThunk(
  "/broadcast/group/delete",
  async (
    { groupIds, page, limit, searchQuery, sortKey, sortDirection, navigate },
    { dispatch }
  ) => {
    try {
      const response = await userGroupService.deleteGroup(groupIds, navigate);
      // refresh list after deletion
      dispatch(
        getAllGroupsData({
          page,
          limit,
          searchQuery,
          sortKey,
          sortDirection,
          navigate,
        })
      );

      toast.success("Groups deleted successfully!");
      return response;
    } catch (error) {
      toast.error(error?.data?.error);
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message
        );
      }
      throw error?.data?.message;
    }
  }
);
export const deleteSingleGroup = createAsyncThunk(
  "/broadcast/group/deleteSingleGroup",
  async (
    { id, page, limit, searchQuery, sortKey, sortDirection, navigate },
    { dispatch }
  ) => {
    try {
      const response = await userGroupService.deleteSingleGroup(id, navigate);
      // refresh list after deletion
      dispatch(
        getAllGroupsData({
          page,
          limit,
          searchQuery,
          sortKey,
          sortDirection,
          navigate,
        })
      );

      toast.success("Group deleted successfully!");
      return response;
    } catch (error) {
      toast.error(error?.data?.error);
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.errors ? error?.data?.errors : error?.data?.message
        );
      }
      throw error?.data?.message;
    }
  }
);

export const getGroupByIdData = createAsyncThunk(
  "broadcast/getGroupById",
  async ({ id, navigate }, { dispatch }) => {
    try {
      // call the service method that hits /broadcast/group/:id
      const response = await userGroupService.getGroupById(id, navigate);
      // response should be your { group: { ... } } object
      return response;
    } catch (error) {
      toast.error(error?.data?.error);

      // handle unauthorized or other errors
      throw error;
    }
  }
);

export const createBulkGroupFromCsv = createAsyncThunk(
  "/broadcast/bulk-user/group",
  async (
    { formData, navigate, page, limit, searchQuery, sortKey, sortDirection },
    { dispatch }
  ) => {
    try {
      const response = await userGroupService.createBulkGroup(formData);

      return response;
    } catch (error) {
      toast.error(
        error?.data?.error ||
          error?.data?.message ||
          "Failed to create group from CSV"
      );
      if (error?.response?.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  }
);

// ------------------------------------
// Slice
// ------------------------------------
const userGroupSlice = createSlice({
  name: "userGroups",
  initialState: {
    loading: false,
    groups: [],
    selectedGroup: null, // If you want to store a single group
    page: 1,
    limit: 10,
    searchQuery: "",
    sortConfig: { key: "name", direction: "asc" },
    previewResult: null,
  },
  reducers: {
    // Synchronous actions
    setSelectedGroup: (state, action) => {
      state.selectedGroup = action.payload;
    },
    clearSelectedGroup: (state) => {
      state.selectedGroup = null;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setLimit: (state, action) => {
      state.limit = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setSortConfig: (state, action) => {
      state.sortConfig = action.payload;
    },
    setPreviewResult: (state, action) => {
      state.previewResult = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      //preview step
      .addCase(createBulkGroupFromCsv.pending, (state) => {
        state.loading = true;
        state.previewResult = null;
      })
      .addCase(createBulkGroupFromCsv.fulfilled, (state, action) => {
        state.loading = false;
        state.previewResult = action.payload || null;
      })
      .addCase(createBulkGroupFromCsv.rejected, (state) => {
        state.loading = false;
        state.previewResult = null;
      })
      // ----- getAllGroupsData -----
      .addCase(getAllGroupsData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllGroupsData.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload?.data || [];
      })
      .addCase(getAllGroupsData.rejected, (state) => {
        state.loading = false;
      })

      // ----- createGroupData -----
      .addCase(createGroupData.pending, (state) => {
        state.loading = true;
      })
      .addCase(createGroupData.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createGroupData.rejected, (state) => {
        state.loading = false;
      })

      // ----- updateGroupData -----
      .addCase(updateGroupData.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateGroupData.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateGroupData.rejected, (state) => {
        state.loading = false;
      })

      // ----- deleteGroupData -----
      .addCase(deleteGroupData.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteGroupData.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteGroupData.rejected, (state) => {
        state.loading = false;
      })
      //THUNK for getGroupByIdData
      .addCase(getGroupByIdData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getGroupByIdData.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedGroup = action.payload?.group || null;
      })
      .addCase(getGroupByIdData.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  setSelectedGroup,
  clearSelectedGroup,
  setPage,
  setLimit,
  setSearchQuery,
  setSortConfig,
} = userGroupSlice.actions;

export default userGroupSlice.reducer;
